import { assertSafeInteger } from '@kenocyber/shared';
import { DAMAGE_MILLI_PER_UNIT } from './base';
import { BPS_ONE } from './exact';

/**
 * **A redução universal de dano, e a fronteira onde ela acontece.**
 *
 * ## O que esta passiva é, e o que ela deliberadamente não é
 *
 * Não é um proc. Não sorteia, não consome entropia, não ocupa intervalo em
 * loteria nenhuma, não tem cooldown e não escolhe um instante. Está ativa
 * enquanto a peça estiver encaixada, e o que ela produz é um MULTIPLICADOR
 * aplicado a todo o dano hostil realmente endereçado a uma nave.
 *
 * Não concede escudo, vida temporária, regeneração nem reflexão. Nada aqui cria
 * estado, absorve uma parcela para mais tarde, ou devolve alguma coisa a
 * alguém: o dano que ela impede simplesmente **não acontece**.
 *
 * ## Porque este ficheiro não conhece nenhuma joia
 *
 * Recebe um `multiplierBps` que `resolveJewelEffects` derivou do perfil
 * congelado. Não sabe o que é `levelSum`, não sabe o que é uma cópia, não lê o
 * catálogo e não tem o id de joia nenhuma escrito em lado nenhum - a mesma
 * disciplina que `damage/lifesteal.ts` e `damage/exact.ts` já têm, e que
 * `jewel-effects-absent.test.ts` verifica módulo a módulo. É o que permite às
 * duas simulações reduzirem dano sem um `if` por joia.
 *
 * ## Porque `bigint`
 *
 * Porque o dano que ela multiplica não tem tecto de jogo: cada Coração
 * Primordial incorporado soma 100 basis points e a contagem vive em
 * `numeric(40, 0)`. Reduzir 60 % de um número com quarenta e dois dígitos num
 * `double` seria capar um item num tipo, que é o que o `CLAUDE.md` proíbe
 * absolutamente.
 *
 * ## As duas fronteiras, e porque são duas
 *
 * Pela mesma razão exata que `resolveStrike` e `resolveIntegerStrike` são duas,
 * e que `resolveLifestealHeal` e `resolveIntegerLifestealHeal` são duas: o duelo
 * mede dano em milli-inteiros e a caça mede a fuga de um mob num `number`
 * fracionário. As duas atravessam para a **mesma** aritmética -
 * {@link reduceHostileDamageMilli} - e é essa a resposta ao §"as duas simulações
 * devem usar exatamente a mesma função matemática".
 */

/* -------------------------------------------------------------------------- */
/*  A lei                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * O multiplicador do dano recebido, dado quanto é reduzido.
 *
 * `10 000 - reducao`, e mais nada. Uma função e não uma subtração escrita em
 * dois sítios, porque é ela que carrega as duas garantias que o resto do
 * ficheiro assume: o resultado está sempre em `[0, 10 000]`, e uma redução que
 * o passasse é recusada em vez de saturada.
 *
 * ## Porque uma redução acima de 100 % é recusada e não cortada
 *
 * Porque cortar legalizaria o dado. O teto da joia é declarado no catálogo e o
 * resolvedor já o aplica por peça; uma redução que chegasse aqui acima de
 * `BPS_ONE` significaria que uma dessas fronteiras falhou, e continuar em
 * silêncio seria construir combate a partir de um estado que não devia existir.
 * É a mesma regra do `CLAUDE.md` sobre `Math.trunc` a aceitar um valor
 * malformado.
 */
export function hostileDamageMultiplierBps(reductionBps: bigint): bigint {
  if (reductionBps < 0n) {
    throw new RangeError(`a damage reduction is never negative, got ${reductionBps}`);
  }
  if (reductionBps > BPS_ONE) {
    throw new RangeError(`a damage reduction never exceeds ${BPS_ONE} bps, got ${reductionBps}`);
  }
  return BPS_ONE - reductionBps;
}

/**
 * **O único sítio onde dano hostil encolhe.** Puro, exato, e sem estado.
 *
 * `dano x multiplicador / 10 000`, truncado UMA vez.
 *
 * ## A identidade, e porque ela é estrutural
 *
 * Sem joia o multiplicador é `BPS_ONE` e a função devolve o argumento **pelo
 * mesmo `bigint`**, sem multiplicar e sem dividir. Não é uma otimização: é a
 * garantia de que um casco sem esta passiva produz exatamente o estado que
 * produzia antes deste gate existir, em todos os ramos - e consegui-lo por
 * construção é diferente de o conseguir por a aritmética calhar devolver o
 * mesmo número.
 *
 * ## O arredondamento, e porque NÃO há carry novo
 *
 * Esta é a mesma operação que `shotDamageMilli` e `jewelDamageMilli` já fazem -
 * um coeficiente em basis points sobre uma magnitude em milli - e a resposta
 * canónica do projeto para ela é truncar uma vez, por instância, sem resto
 * guardado.
 *
 * O resto que ela larga é **sub-milli**: menos de um milésimo de um ponto de
 * vida, sobre um payload que vale dezenas de milhares de milli. E ele é largado
 * UMA vez sobre uma magnitude independente - nunca repetidamente sobre um total
 * a acumular - portanto não há arredondamento repetido a compor.
 *
 * O que `damage/lifesteal.ts` carrega em `lifestealCarryBps` é um caso diferente
 * e a diferença é a razão: lá o RESULTADO pode ser menor do que um milli - 9 %
 * de um golpe pequeno - e sem resto guardado a joia curaria zero, isto é,
 * deixaria de existir. Aqui o resultado é entre 40 % e 100 % da entrada, e nunca
 * pode arredondar para nada.
 *
 * E a fração que sobra a seguir não se perde: o resto entre milli e ponto
 * inteiro fica no casco, em `damageCarryMilli`, que é o carry canónico que o
 * duelo já persiste. Esta função entrega a essa fronteira, e a fronteira fecha.
 */
export function reduceHostileDamageMilli(damageMilli: bigint, multiplierBps: bigint): bigint {
  if (damageMilli < 0n) {
    throw new RangeError(`hostile damage is never negative, got ${damageMilli}`);
  }
  if (multiplierBps < 0n || multiplierBps > BPS_ONE) {
    throw new RangeError(`a damage multiplier is in [0, ${BPS_ONE}] bps, got ${multiplierBps}`);
  }
  if (multiplierBps === BPS_ONE) {
    /* Sem redução, o payload é o MESMO valor. Ver a nota acima. */
    return damageMilli;
  }
  return (damageMilli * multiplierBps) / BPS_ONE;
}

/**
 * **A mesma lei, para uma simulação cujo dano é um `number` fracionário.**
 *
 * A caça. A fuga de um mob vale uma fração da vida máxima da nave e sempre foi
 * um `number` - a física da caça é em vírgula flutuante desde que existe, e
 * migrá-la seria mudar todos os números do jogo para resolver um problema que
 * não está lá.
 *
 * ## Porque atravessa para milli em vez de multiplicar em `number`
 *
 * Porque o gate exige que as duas simulações usem **a mesma função**, e um
 * `x * 4000 / 10000` escrito aqui seria uma segunda fórmula a concordar com a
 * primeira por coincidência. A travessia é para a unidade que o dano já tem no
 * resto do domínio, e a decisão é tomada uma vez, em `bigint`.
 *
 * A quantização ao milésimo é mais fina do que a que o próprio dano da caça já
 * tem - `totalDamagePerProjectileMilli` arredonda para milli desde o V28F.B - e
 * ela só acontece a quem TEM a joia: sem ela, a identidade acima devolve o
 * `number` de entrada intacto, bit a bit.
 */
export function reduceHostileDamage(damage: number, multiplierBps: bigint): number {
  if (!Number.isFinite(damage)) {
    throw new RangeError(`hostile damage must be finite, got ${damage}`);
  }
  if (damage < 0) {
    throw new RangeError(`hostile damage is never negative, got ${damage}`);
  }
  if (multiplierBps === BPS_ONE || damage === 0) {
    /* Sem redução, o valor é o MESMO `number`. Nem escala, nem arredonda. */
    return damage;
  }
  const scaled = Math.round(damage * DAMAGE_MILLI_PER_UNIT);
  /*
   * Afirmado e não arredondado por omissão, exatamente como `shipBaseDamageMilli`
   * faz: um dano que não sobrevive a `x 1000` é um estado que ninguém provou ser
   * exprimível, e recusá-lo é a regra do `CLAUDE.md` sobre valores malformados.
   */
  assertSafeInteger(scaled, 'hostile damage scaled to milli');
  return Number(reduceHostileDamageMilli(BigInt(scaled), multiplierBps)) / DAMAGE_MILLI_PER_UNIT;
}

/* -------------------------------------------------------------------------- */
/*  A tabela fechada: o que é dano hostil, e o que é redutível                */
/* -------------------------------------------------------------------------- */

/**
 * As origens hostis que a CAÇA pode resolver contra a nave.
 *
 * Uma, hoje. A passagem do boss é morte incondicional e não é dano, portanto não
 * está aqui: reduzir uma coisa que não é dano seria dar à joia o poder de adiar
 * uma regra que não fala de magnitude nenhuma.
 *
 * Um ataque de mob ou de boss - um projétil, um contacto, uma área - não existe
 * em build nenhum. O dia em que existir é o dia em que tem de vir cá
 * declarar-se, e é a prova de completude que o obriga.
 */
export const HUNT_HOSTILE_DAMAGE_SOURCES = [
  /** Um mob que atravessa a fronteira de baixo e cobra à nave. */
  'mob_leak',
] as const;

export type HuntHostileDamageSource = (typeof HUNT_HOSTILE_DAMAGE_SOURCES)[number];

/**
 * As origens hostis que o DUELO pode resolver contra um casco, transcritas.
 *
 * ## Porque transcritas em vez de importadas
 *
 * Porque `damage/reflection.ts` é do duelo, e `hunt/reflection-absent.test.ts`
 * prova que ele **não entra no grafo de imports da caça**. Este módulo é
 * importado pelas duas simulações; importar a Reflexão a partir daqui desfaria
 * essa fronteira em silêncio, e a fronteira é uma decisão do ADR 0058.
 *
 * ## Redução e Reflexão são classificações TOTAIS e INDEPENDENTES
 *
 * As duas tabelas foram iguais durante dez origens, e a igualdade foi
 * confundida com a regra. Não é: um dano pode ser reduzido sem ser
 * reflectível, e pode ter política própria de reflexão. Cada tabela tem de
 * ser **completa sobre o que a sua fronteira resolve**, e nenhuma tem de
 * concordar com a outra sobre o conjunto.
 *
 * O que impede esta cópia de envelhecer não é uma igualdade: é
 * `Record<HostileDamageSource, HostileDamagePolicy>`, que não compila com uma
 * origem por decidir, mais a prova de que toda a origem conhecida — venha ela
 * do duelo, da caça ou do quadrante — está declarada nas duas.
 */
export type PvpHostileDamageSource =
  | 'cannon_shot'
  /**
   * **O contacto de uma criatura hostil que não é um jogador.**
   *
   * O mob e o chefe do mundo aberto encostam-se e ferem pelo simples facto de
   * estarem encostados. É dano hostil ordinário, e por isso a redução canónica
   * do casco vale sobre ele como vale sobre um tiro — decisão do dono no gate
   * V28I.BW.4A.
   *
   * A produção já o fazia: o contacto entra por `applyDamage`, que é a
   * fronteira única do quadrante e onde `reduceHostileDamageMilli` é chamada.
   * O que faltava era a DECLARAÇÃO, e é ela que esta linha é.
   */
  | 'creature_contact'
  | 'cyclone_cut'
  | 'saw_contact'
  | 'poison_application'
  | 'poison_tick'
  | 'bleed_application'
  | 'bleed_detonation'
  | 'missile_impact'
  | 'atomic_bomb_proximity'
  | 'atomic_blast_splash';

export type HostileDamageSource = PvpHostileDamageSource | HuntHostileDamageSource;

/**
 * **Toda a origem de dano hostil que uma simulação pode resolver contra uma
 * nave, nos DOIS modos.**
 *
 * ## Porque uma lista e não um `if` em cada call site
 *
 * Porque o §"não espalhe condicionais pelos call sites" é uma afirmação sobre
 * completude, e completude não se verifica lendo call sites. A redução é
 * aplicada numa fronteira só por modo - `applyDamage` no duelo, a travessia da
 * fronteira de baixo na caça - e esta tabela é a prova de que essas fronteiras
 * cobrem tudo: cada origem declara o que é, e uma origem nova que não declare
 * **não compila**.
 */
export const HOSTILE_DAMAGE_SOURCES: readonly HostileDamageSource[] = Object.freeze([
  'cannon_shot',
  'creature_contact',
  'cyclone_cut',
  'saw_contact',
  'poison_application',
  'poison_tick',
  'bleed_application',
  'bleed_detonation',
  'missile_impact',
  'atomic_bomb_proximity',
  'atomic_blast_splash',
  ...HUNT_HOSTILE_DAMAGE_SOURCES,
]);

/**
 * O que a tabela responde sobre uma origem.
 *
 * Uma união e não um booleano, pela mesma razão que a da Reflexão é: as duas
 * respostas não são a mesma coisa negada. Uma origem redutível diz ONDE a
 * redução acontece; uma que não o fosse teria de dizer porquê, e um `false`
 * partilhado apagaria a razão.
 */
export type HostileDamagePolicy =
  | { readonly reducible: true; readonly boundary: string }
  | { readonly reducible: false; readonly reason: string };

/**
 * **A fronteira única do duelo**, nomeada uma vez e partilhada por dez linhas.
 *
 * Dez origens e uma só resposta, e escrevê-la assim é a afirmação: elas passam
 * todas por `PvpSimulation.applyDamage`, que é o único sítio do duelo onde um
 * payload encontra a vida de um casco. Dez frases diferentes sugeririam dez
 * sítios.
 */
const PVP_APPLY_DAMAGE: HostileDamagePolicy = Object.freeze({
  reducible: true,
  boundary:
    'a fronteira única de aplicação de dano do duelo, depois de a Reflexão ter ' +
    'escolhido o destinatário final e antes do clamp pela vida restante',
});

/**
 * **A tabela fechada.** Uma linha por origem, sem default e sem fallthrough.
 *
 * `Record<HostileDamageSource, HostileDamagePolicy>` é o que a torna fechada:
 * acrescentar uma origem sem decidir se ela é redutível é um erro de
 * compilação, e não uma omissão que cai numa permissão silenciosa.
 *
 * **Todas reduzem**, e a uniformidade é a decisão do gate: a redução vale
 * sobre *todo* dano hostil realmente destinado à nave, sem tabela de exceções
 * e sem origem privilegiada. A tabela existe na mesma - e é exaustiva na mesma
 * - porque o valor dela não é a variedade das respostas: é obrigar a origem
 * seguinte a vir aqui dizer a sua.
 *
 * O que NÃO está aqui não está por construção e não por omissão: uma morte
 * imposta — a ultimate de um chefe final, uma eliminação, um abandono — não é
 * um payload. Ela não atravessa fronteira de dano nenhuma, e a lista ao lado,
 * {@link NON_HOSTILE_HEALTH_OPERATIONS}, é onde essa ausência é afirmada.
 */
export const HOSTILE_DAMAGE_REDUCTION: Readonly<Record<HostileDamageSource, HostileDamagePolicy>> =
  Object.freeze({
    cannon_shot: PVP_APPLY_DAMAGE,
    /*
     * **O quadrante, e a fronteira dele é uma terceira.**
     *
     * Um contacto de criatura só existe no mundo aberto, e ali todo o dano
     * passa por `OpenWorldSimulation.applyDamage` — o porto, a protecção de
     * fronteira, a Marca da Caveira e a redução são decididos lá dentro, uma
     * linha e não uma linha por caminho.
     */
    creature_contact: Object.freeze({
      reducible: true,
      boundary:
        'a fronteira única de aplicação de dano do quadrante, depois da Marca da Caveira do atacante e antes do clamp pela vida restante',
    }),
    cyclone_cut: PVP_APPLY_DAMAGE,
    saw_contact: PVP_APPLY_DAMAGE,
    poison_application: PVP_APPLY_DAMAGE,
    poison_tick: PVP_APPLY_DAMAGE,
    bleed_application: PVP_APPLY_DAMAGE,
    bleed_detonation: PVP_APPLY_DAMAGE,
    missile_impact: PVP_APPLY_DAMAGE,
    atomic_bomb_proximity: PVP_APPLY_DAMAGE,
    atomic_blast_splash: PVP_APPLY_DAMAGE,
    /*
     * A caça, e a sua fronteira é a outra.
     *
     * O `damage` que o evento de fuga publica é já o valor reduzido - o mesmo
     * número que saiu da vida. Publicar o bruto e reduzir depois faria a cena
     * imprimir um golpe que não aconteceu.
     */
    mob_leak: Object.freeze({
      reducible: true,
      boundary:
        'a travessia da fronteira de baixo, antes de a vida ser descontada e ' +
        'antes de o evento de fuga publicar a magnitude',
    }),
  });

/** Lê a tabela. A única forma de a pergunta ser feita. */
export function hostileDamagePolicy(source: HostileDamageSource): HostileDamagePolicy {
  return HOSTILE_DAMAGE_REDUCTION[source];
}

/* -------------------------------------------------------------------------- */
/*  E o que NÃO é dano hostil                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **As operações que mexem na vida de uma nave e não são um golpe.**
 *
 * ## Porque esta lista existe ao lado da outra
 *
 * Porque "isto não é reduzido" é uma afirmação sobre a AUSÊNCIA de uma chamada,
 * e ausências apagam-se sozinhas. Alguém que passasse a encaminhar um abandono
 * pelo caminho de dano estaria a dar a uma joia o poder de adiar o fim de uma
 * partida, e nada no compilador se oporia.
 *
 * Nenhuma destas atravessa a fronteira de aplicação de dano, e é isso que as
 * torna irredutíveis - não uma condição escrita algures. A prova afirma-o
 * medindo: com a passiva ao máximo, cada uma delas produz exatamente o mesmo
 * estado que produz sem ela.
 */
export const NON_HOSTILE_HEALTH_OPERATIONS: Readonly<Record<string, string>> = Object.freeze({
  /** `PvpSimulation.eliminateParticipant`: põe a vida a zero, sem magnitude. */
  elimination:
    'uma eliminação DEFINE a vida como zero; não há payload, não há autor e não ' +
    'há magnitude para multiplicar',
  /** O abandono, que elimina em vez de ferir. */
  abandonment: 'um abandono elimina, e eliminar não é causar dano',
  /** O fim de uma sala por tempo. */
  timeout: 'um tempo esgotado encerra a partida; ele não endereça nave nenhuma',
  /** O encerramento administrativo de uma sala. */
  administrative_close: 'um encerramento administrativo não é um golpe de ninguém',
  /** `HuntSimulation.devForceDeath`, e todo o utensílio equivalente. */
  dev_kill:
    'um comando de teste termina a corrida pelo caminho de morte comum; ele não ' +
    'aplica dano nenhum, e reduzi-lo faria uma ferramenta depender de um perfil',
  /**
   * **A ultimate de um chefe final**, que mata em vez de ferir.
   *
   * `executeUltimateKill` chama `destroy` directamente, e a razão está escrita
   * ao lado dela: um número gigantesco seria reduzido pela defesa, encolhido
   * pela Marca, reflectido pela Égide e sujeito a roubo de vida — e escondê-lo
   * assim seria pedir que a mecânica falhasse em silêncio no dia em que alguém
   * somasse mais uma redução.
   *
   * Ela é nomeada aqui **por causa do gate V28I.BW.4A**: pôr o contacto de uma
   * criatura entre as redutíveis não a arrasta com ele, e a excepção deixa de
   * depender de alguém se lembrar de que ela existe.
   */
  final_boss_ultimate:
    'uma ultimate de chefe final IMPÕE a morte; ela não tem magnitude, não atravessa a fronteira de aplicação de dano, e reduzi-la seria adiar uma regra que não fala de números',
  /** Realocar pontos, trocar equipamento, reiniciar: a vida é DEFINIDA. */
  direct_health_assignment:
    'comprar vida, trocar equipamento, reiniciar ou reconciliar DEFINEM a vida a ' +
    'partir do máximo e do dano já sofrido; nenhuma delas causa dano hostil',
});
